import type { HelpTopic } from '../types';

/**
 * Help for Home, My Workspace, Projects and Tasks screens.
 * Written for office staff: short sentences, the screen's own labels.
 */
export const workspaceHelp: HelpTopic[] = [
  // ════════════════════════════════════════════════════════════════════
  // Home
  // ════════════════════════════════════════════════════════════════════
  {
    path: '/',
    title: 'Home',
    summary:
      'Your start page after login. At the top, Your overview shows a card for every module you can open. Analysts and employees also see their own work below it. Admin, HR and Accounts see the cards only: each module\'s Dashboard (first entry in that module\'s menu) has the detail.',
    sections: [
      {
        title: 'Your overview',
        bullets: [
          'Every login sees Your overview at the top of Home.',
          'There is one card for each module your role may open (for example My Workspace, Projects, HR, Payroll). A module you cannot open has no card.',
          'Each card shows up to four headline numbers for that module. A green tick, orange warning or red sign next to a number tells you it is good, needs attention, or is a problem.',
          'Click a card to open that module\'s dashboard, with more numbers, charts and short lists.',
          'If the cards do not load, press Retry in the red message.',
        ],
      },
      {
        title: 'Admin and HR',
        bullets: [
          'Home shows the module cards only, so no number appears twice.',
          'What used to be on the separate Dashboard screen now lives in the module dashboards: leave, on leave today, exits, headcount and birthdays in HR; candidates to screen in Recruitment; reimbursements in Billing; the salary run in Payroll.',
          'The old Dashboard link (/dashboard) now opens Home.',
        ],
      },
      {
        title: 'Analyst',
        bullets: [
          'At the top are two quick buttons: + Add Leave and + Add Voucher.',
          'MY PROJECTS lists the projects of the current financial year (shown in the title). Use the tabs Not Started, Assigned, In Progress, Cancelled, On Hold and Completed to switch the list.',
          'Click the arrow on a row to see Client Name, Job Value and Milestones. Click view details to open the project.',
          '+ Add Project opens the short Add Project form.',
          'My performance shows your last 8 weeks: hours logged against the hours you had available, hours planned, and tasks finished (and how many on time, by the due date). The bars compare planned and logged hours week by week. Timesheet opens your timesheet.',
          'The Event Calendar box says "Coming Soon". It is not ready yet.',
        ],
      },
      {
        title: 'Employee (and new roles such as site engineer)',
        bullets: [
          'At the top are two quick buttons: + Add Leave and + Add Voucher.',
          'My performance shows your last 8 weeks: hours logged against the hours you had available, hours planned, and tasks finished (and how many on time, by the due date). The bars compare planned and logged hours week by week. Timesheet opens your timesheet.',
          'Attendance log shows your last 7 days, newest first: status (Present, Absent, Paid leave, Holiday, Weekly off …), punch in and out times, hours worked, and a Late mark. Punch in / out opens My Attendance, where you punch and see the whole month.',
          'Each box shows only if your role has the screen behind it (My Work, My Attendance).',
          'MY DAILY TASKS shows the tasks given to you, week by week. Choose the month at the top right, then click a week button. Past weeks cannot be chosen.',
          'Open a project row to see its tasks: Work timeline, Priority, Inward documents and the Note / Comment from the analyst.',
          'Press Play to start work on a task and Pause to stop. The button changes when you press it.',
          'Click the project name to open the project page.',
          'For your full day plan, timers and time logging, use My Workspace → My Work.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Every module in the left menu starts with a Dashboard entry. It opens the same page as that module\'s card here.',
          'The menu on the left shows only the screens your role may open. If you need a screen that is missing, ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Module dashboards', path: '/dashboards/my' },
      { label: 'My Work', path: '/my-work' },
      { label: 'Projects', path: '/work/projects' },
    ],
  },

  {
    path: '/dashboards/:module',
    title: 'Module dashboard',
    summary:
      'One page per module (My Workspace, Projects, Tasks, HR, Recruitment, Payroll, Billing, Accounts, Website, Admin) with its headline numbers, charts and short lists. Open it from the Dashboard entry at the top of each module in the left menu, or from a card on Home.',
    sections: [
      {
        title: 'What it shows',
        bullets: [
          'The top row is the module\'s headline numbers. A coloured edge and icon mark a number that is good (green tick), needs attention (orange warning) or is a problem (red sign).',
          'Money is in rupees with Indian grouping (₹12,34,567). h means hours, d means days.',
          'Below are charts: bars compare items, lines show change over time, a ring shows how a whole is split. Point at a bar, line or slice to read its exact value.',
          'The table button at the top right of a chart shows the same figures as a table.',
          'At the bottom are short lists, for example what is waiting or the top items.',
        ],
      },
      {
        title: 'Only what your role may open',
        bullets: [
          'You see a dashboard only for the modules your role has at least one screen of. Opening another module\'s dashboard says "You do not have access to this module".',
          'A number, chart title or list row opens its screen when your role may open that screen. If it is plain text, your role does not have that screen. Ask the admin if you need it.',
        ],
      },
      {
        title: 'Step by step: check and act',
        steps: [
          'Open the module in the left menu and click Dashboard.',
          'Look for orange or red marks in the top row.',
          'Click that number. The screen behind it opens.',
          'Do the work there, then come back and press Refresh. The time next to Refresh shows when the figures were worked out.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'The figures do not update by themselves. Press Refresh to get the latest.',
          'If the figures do not load, press Retry in the red message.',
        ],
      },
    ],
    related: [{ label: 'Home', path: '/' }],
  },

  // ════════════════════════════════════════════════════════════════════
  // My Workspace
  // ════════════════════════════════════════════════════════════════════
  {
    path: '/my-work',
    title: 'My Work',
    summary:
      'Your own day or week: how many hours you have, what is planned, how much you have logged, and the tasks to work on. Start and pause timers and log time here.',
    sections: [
      {
        title: 'Reading the screen',
        bullets: [
          'Day / Week switches between one day and the whole week. Use the arrows or the date box to move. Today brings you back to today.',
          'Capacity: your working hours for the day (by default 8.5 hours on a normal day; less on leave; 0 on a holiday or weekly off).',
          'Allocated: hours planned for you. Actual: hours you logged. Remaining: hours still planned. Utilization: allocated ÷ capacity.',
          'The status chip: Underutilized (70% or less), Normal (up to 90%), High (up to 100%), Overloaded (over 100%).',
          'TASKS table: project and task, Priority, Status, hours planned for this day or week, hours logged, and Allocated / Actual / Left for your assignment. A red Due date means the task is overdue.',
          'OTHER WORK table: time you logged for meetings, training and other non-task work.',
        ],
      },
      {
        title: 'Step by step: accept a new task',
        steps: [
          'A blue box says "A new task awaits your acceptance". Click Open it to read the task first if you want.',
          'Click Accept. The task now shows in your TASKS list.',
          'If someone is handing you their task, the blue box shows who and by which date to answer. Click Accept, or Details to read it and decline there.',
        ],
      },
      {
        title: 'Step by step: track your time',
        steps: [
          'In the TASKS table, press the green play button on the task you start. A green bar shows "Timer running on …".',
          'When you stop for a break or switch work, press the pause button (or Pause in the green bar).',
          'To add time by hand, press the clock icon on the task row, or Log time at the top. Fill the LOG TIME form and press Log.',
          'For a meeting, training or office work that is not a task, press the people icon next to Log time. Choose the Category and press Log.',
        ],
      },
      {
        title: 'Fields (LOG TIME / LOG OTHER WORK)',
        fields: [
          { name: 'Task', help: 'The task you worked on. Only tasks you have accepted are listed.', required: true },
          { name: 'Category', help: 'Only in LOG OTHER WORK. What the time was spent on, for example a meeting. It counts on your timesheet, not on any task.', required: true },
          { name: 'Date', help: 'The day you did the work. Today or an earlier day. A future date is not allowed.', required: true },
          { name: 'From – to / Hours', help: 'Choose one. From – to: start and end time (end must be after start). Hours: a number in steps of 0.25, more than 0 and at most 24.', required: true },
          { name: 'Notes', help: 'A short note about the work. Optional.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'You must Accept a task before you can start its timer or log time on it.',
          'A task that is Submitted for review cannot be restarted until the analyst requests a revision.',
          'If a task is blocked (for example it waits on another task that is not finished), the play button does not show.',
          'Time cannot be added to a week whose timesheet is submitted or approved.',
          'Time on a completed or cancelled task cannot be logged.',
          'Click the pencil icon on a task row to open the full task page.',
        ],
      },
    ],
    related: [
      { label: 'My Timesheet', path: '/timesheet' },
      { label: 'Task details', path: '/tasks/:id' },
      { label: 'Reassignments', path: '/tasks/reassignments' },
    ],
  },
  {
    path: '/timesheet',
    title: 'My Timesheet',
    summary:
      'Your week as a grid of tasks and days. Check the hours, add missing time, and submit the week to your manager for approval.',
    sections: [
      {
        title: 'Reading the screen',
        bullets: [
          'Use the arrows to move one week back or forward. This week brings you back.',
          'The chip next to the week shows its status, for example Draft, Submitted, Approved or Rejected (returned).',
          'The grid has one row per task, and one "Other work" row per category. Each column is a day.',
          'Logged is your total per day. Capacity is the hours you should work that day.',
          'Below the grid is the list of every time entry with date, from – to, hours, type and notes.',
        ],
      },
      {
        title: 'Step by step: submit your week',
        steps: [
          'Open the week. Check that every working day has the hours you worked.',
          'Missing time on a task? Press Log time, fill the form, press Log.',
          'Missing a meeting or training? Press Other work, choose the Category, press Log.',
          'Wrong entry? Press the bin icon on that entry to delete it, then log it again.',
          'Stop any running timer first.',
          'Press Submit week. The week is now locked and goes to your manager.',
        ],
      },
      {
        title: 'Fields (Log time / Other work)',
        fields: [
          { name: 'Task', help: 'The task you worked on. Only your active tasks are listed.', required: true },
          { name: 'Category', help: 'Only for Other work: meeting, training, office work and so on.', required: true },
          { name: 'Date', help: 'A day of this week, today or earlier.', required: true },
          { name: 'From – to / Hours', help: 'Either start and end time, or a number of hours (0.25 to 24).', required: true },
          { name: 'Notes', help: 'Optional short note.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Log time, Other work and Submit week show only while the week can be changed (not yet submitted, or returned to you).',
          'A future week cannot be submitted.',
          'After Submit week you cannot add, change or delete time in that week.',
          'If your manager returns the week, a yellow box shows who returned it and why. Correct the time and press Submit week again.',
          'When approved, a green box shows who approved it and when.',
        ],
      },
    ],
    related: [
      { label: 'My Work', path: '/my-work' },
      { label: 'Team Timesheets', path: '/team-timesheets' },
    ],
  },
  {
    path: '/my-attendance',
    title: 'My Attendance',
    summary:
      'Punch in when you reach office and punch out when you leave. See your month as a coloured calendar, your late marks, and the year\'s holidays.',
    sections: [
      {
        title: 'Step by step: punch in and out',
        steps: [
          'When you start work, open My Attendance and press Punch In. The TODAY card shows your In time.',
          'When you leave, press Punch Out. The card shows your Out time and the hours Worked.',
          'If you press Punch Out again later, the later time is kept. The last punch out of the day counts.',
        ],
      },
      {
        title: 'Calendar colours',
        bullets: [
          'Green: Present (a full day worked).',
          'Yellow: Half day.',
          'Red: Absent.',
          'Blue: Paid leave. Purple: Unpaid leave. Light blue: Half-day leave.',
          'Teal: Holiday (the holiday name is shown). Grey: Weekly off.',
          'Very light grey: Upcoming (a future day). Light green "In office": today, while you are punched in.',
          'A blue border marks today. An orange clock icon marks a late day.',
          'Point the mouse at a day to see your in and out times, hours worked, leave type, and any change made by HR ("Set by HR").',
        ],
      },
      {
        title: 'How the day is decided (office rules)',
        bullets: [
          'Late mark: punch in after the office start time plus the grace minutes. By default office starts at 09:30 with 15 minutes grace.',
          'Full day: by default 8 hours or more between punch in and punch out. Half day: 4 hours or more. Less than that is Absent.',
          'Forgot to punch out? A past day with no punch out counts as a Half day by default.',
          'No punch and no approved leave on a working day counts as Absent.',
          'Late marks cost pay: by default every 3 late marks in a month = half a day loss of pay (LOP).',
          'HR sets these rules. The numbers on your screen follow the rules in force.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'The time is taken from the server, not your computer. Changing your PC clock does not change your punch.',
          'You can punch in only once a day. You cannot punch out before you punch in.',
          'If the month\'s salary is already paid, punching is closed. Ask HR to record your day.',
          'Wrong or missing punch? Ask HR to correct it on the Attendance Register.',
          'The chips above the calendar count Working days, Present, Half days, Paid leave, Unpaid leave, Absent, Holidays, Weekly offs, Late marks and LOP days for the month.',
          'Use the month picker to see an earlier month. Apply for leave takes you to My Leaves.',
        ],
      },
    ],
    related: [
      { label: 'My Leaves', path: '/my-leaves' },
      { label: 'My Salary', path: '/my-salary' },
    ],
  },
  {
    path: '/my-leaves',
    title: 'My Leaves',
    summary:
      'Apply for leave, see your leave balance, and follow each application until it is approved or rejected.',
    sections: [
      {
        title: 'Step by step: apply for leave',
        steps: [
          'Check MY LEAVE BALANCE first. Available shows the days you still have this financial year.',
          'Press + Add Leave. The "Applying for leave" form opens.',
          'Choose the Leave type.',
          'Pick the Start date and its session (first half, second half or full day).',
          'For more than one day, pick the End date and its session. For one day only, leave End date empty.',
          'Write the Reason. Attach a file if you have one (for example a medical certificate).',
          'Press Save. The application shows under Active with status Pending.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Leave type', help: 'Casual, Sick, Earned, Compensatory Off, Maternity, Paternity or Leave Without Pay. Under the box you see how many days are Available; you cannot apply for more than that. Leave Without Pay has no limit and is cut from salary.', required: true },
          { name: 'Start date', help: 'The first day of leave. Today or a later date.', required: true },
          { name: 'Start date session', help: 'Session 1 (First Half of the day), Session 2 (Second Half of the day) or Full Day.', required: true },
          { name: 'End date', help: 'The last day of leave. Must be after the start date. Leave it empty for a single day.' },
          { name: 'End date session', help: 'Required only when you pick an End date.' },
          { name: 'Reason', help: 'Why you need leave. 3 to 600 characters.', required: true },
          { name: 'Attach file', help: 'Optional. PDF, Word, Excel or PowerPoint file.' },
        ],
      },
      {
        title: 'Statuses',
        bullets: [
          'Pending: waiting for HR or the admin. Shown under Active.',
          'Approved: shown under Closed → Approved.',
          'Rejected: shown under Closed → Rejected. Open the row to read the Reason for Rejection.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'HR or the admin approves leave on HR → Leave Approvals.',
          'You cannot apply for a date in the past here. For a past day, ask HR.',
          'The form does not stop you if your balance is low. Check Available before you apply.',
          'Types marked "Not tracked" (for example leave without pay, compensatory off) have no quota and are not counted down.',
          'There is no edit or cancel button here. To change or cancel a leave, ask HR.',
          'Approved leave shows on your My Attendance calendar as Paid leave or Unpaid leave.',
        ],
      },
    ],
    related: [
      { label: 'My Attendance', path: '/my-attendance' },
      { label: 'Home', path: '/' },
    ],
  },
  {
    path: '/my-salary',
    title: 'My Salary',
    summary:
      'Your salary for each month that has been paid through payroll, with a full breakdown of how it was worked out.',
    sections: [
      {
        title: 'Step by step: check a month',
        steps: [
          'PAID MONTHS lists every paid month with Gross, Deductions, Net and the Paid on date.',
          'Click a month. The BREAKDOWN opens on the right.',
          'Read the day counts: Present, Half days, Paid leave, Unpaid leave, Absent, Late marks, LOP days and Payable days.',
          'Read Earnings, Deductions and the Net pay at the bottom.',
        ],
      },
      {
        title: 'How the amount is worked out',
        bullets: [
          'Earned gross = Monthly gross × Payable days ÷ Pay days. The line above the tables shows these numbers.',
          'Earnings shows each part of the salary for the full month and what you earned.',
          'Deductions shows PF, ESI, professional tax, TDS, advance recovery and so on.',
          'Employer contributions are paid by the company. They are not deducted from your pay.',
          'At the bottom you see your PAN, UAN, ESIC number and the bank account the salary was paid to.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Only paid months are shown. A month that is calculated but not yet paid does not appear.',
          'If you see "No salary has been paid through payroll yet", no month has been paid through this system yet.',
          'For a salary slip (PDF), ask the accounts department.',
          'If your days look wrong, check My Attendance and talk to HR.',
        ],
      },
    ],
    related: [{ label: 'My Attendance', path: '/my-attendance' }],
  },
  {
    path: '/my-reimbursements',
    title: 'My Reimbursements',
    summary:
      'Claim money you spent for office work (site visit, travel, supplies and so on) and follow each claim until it is approved or rejected.',
    sections: [
      {
        title: 'Step by step: raise a claim',
        steps: [
          'Press + Add Voucher. The "Request voucher expense" form opens.',
          'Choose the Expense type.',
          'Type the Amount in rupees.',
          'Pick the Date of event, the day you spent the money.',
          'Add a Note if needed.',
          'Press Attach file and choose the bill.',
          'Press Save. The claim shows under Active with status Pending.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Expense type', help: 'Office supplies, Gift voucher, Site visit, Hotel expenses, Travel expenses, Training and education expense, Dues and subscription expense, Utilities or Other.', required: true },
          { name: 'Amount', help: 'The amount in ₹. Numbers only, at least ₹1.', required: true },
          { name: 'Date of event', help: 'When you spent the money. Today or an earlier date.', required: true },
          { name: 'Note (if any)', help: 'Optional. If you write one, 3 to 600 characters. Say what it was for, for example the site name.' },
          { name: 'Attach file', help: 'The bill or receipt. The file must be PDF, Word, Excel or PowerPoint.' },
        ],
      },
      {
        title: 'Statuses',
        bullets: [
          'Pending: waiting for approval. Shown under Active.',
          'Approved or Rejected: shown under Closed. Open a rejected row to read the Reason for Rejection.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Claims are approved on Billing → Reimbursement by the person with approval rights.',
                    'The bill can be a photo (JPG, PNG), a PDF or an Office file.',
          'Always attach the bill. A claim without proof may be rejected.',
          'There is no edit button. If you made a mistake, ask the approver to reject it and raise a new claim.',
        ],
      },
    ],
    related: [
      { label: 'Home', path: '/' },
      { label: 'Site Visits', path: '/site-visits' },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // Projects
  // ════════════════════════════════════════════════════════════════════
  {
    path: '/work/projects',
    title: 'Projects',
    summary:
      'The list of jobs (projects). Analysts and office staff see MY PROJECTS by status, for the current financial year only. Employees see MY DAILY TASKS, their tasks for the week grouped by project.',
    sections: [
      {
        title: 'MY PROJECTS (analyst and office)',
        bullets: [
          'Click a tab to change the list: Not Started, Assigned, In Progress, Cancelled, On Hold, Completed.',
          'Columns: Job Code, Job Date, Project Name, Job Status. Click a column title to sort.',
          'Click the arrow on a row to see Client Name, Job Value and Milestones (done / total). Click view details to open the project.',
          'Only projects dated in the current financial year (April to March) are listed; the year is in the title. Older projects are in Project Report.',
          '+ Add Project: the short form. Fill Work Details and the Quotation later from Project Report.',
        ],
      },
      {
        title: 'MY DAILY TASKS (employee)',
        bullets: [
          'Choose the month at the top right, then click a week. Past weeks are greyed out.',
          'Each row is a project with its Time Left and Status. Open the row to see your tasks.',
          'Each task shows Work timeline, Priority, Inward documents and the analyst\'s Note / Comment.',
          'Press Play to start work and Pause to stop.',
        ],
      },
      {
        title: 'Step by step: create a project',
        steps: [
          'Press + Add Project and fill the basic details.',
          'Then open the job in Project Report → Job actions for Work Details and the Quotation.',
          'After saving, find the job in MY PROJECTS (if dated this financial year) or in Project Report.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          '+ Add Project shows only for roles allowed to edit jobs. If you don\'t see it, your role doesn\'t have that action — ask the admin.',
          'To search all jobs with filters or export to Excel, use Project Report.',
        ],
      },
    ],
    related: [
      { label: 'Add Project', path: '/work/projects/add' },
      { label: 'New Job Creation', path: '/work/projects/new' },
      { label: 'Project Report', path: '/work/report' },
    ],
  },
  {
    path: '/work/projects/add',
    title: 'Add Project',
    summary:
      'The short way to create a job: basic details, type of work, contacts (POC) and status. Quotation, floors and milestones can be added later from Project Report.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Fill Basic Details: Job Code, Job Name, Job Date, Company, Locality and Site Address.',
          'Under Type of Work, pick the Project Type, Construction Work Type and Building Type if you know them.',
          'Under POC, pick the Client, Architect, Builder and Owner.',
          'Job Status starts as "Not Started". Change it only if needed.',
          'Press Save Project. A message says "Project … created successfully".',
          'Press OK. You go back to the Projects list.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Job Code', help: 'The office job number. Up to 50 characters. It must be new — a code already used shows an error on this field.', required: true },
          { name: 'Job Name', help: 'Name of the project, 3 to 100 characters.', required: true },
          { name: 'Job Date', help: 'Set to today. Change it if the job started on another day.', required: true },
          { name: 'Financial Year', help: 'Filled by itself from the Job Date (April to March). You cannot type here.' },
          { name: 'Company', help: 'Our company that takes the job.', required: true },
          { name: 'Locality', help: 'The area of the site. Pick from the list, or use + Add to add a new one.', required: true },
          { name: 'Site Address', help: 'Full site address, 3 to 300 characters.', required: true },
          { name: 'Project Type', help: 'For example new or existing work. Optional.' },
          { name: 'Construction Work Type', help: 'Building work, allied work, or both. Optional here, but needed later before Work Details can be filled.' },
          { name: 'Building Type', help: 'Residential, commercial and so on. Optional.' },
          { name: 'Client / Architect / Builder / Owner', help: 'Pick the contact from the list. If the person is new, use + Add next to the field. Optional.' },
          { name: 'Status', help: 'Starts as Not Started.' },
          { name: 'Cancellation Reason', help: 'Appears only when Status is Canceled. Say why the job is cancelled.', required: true },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Every list has + Add and a pencil to add or correct an entry without leaving the form.',
          'Only the admin can add or edit a Company. If you don\'t see + Add on Company, your role doesn\'t have that action — ask the admin.',
          'Cancel asks "Discard this project?" if you typed something. Choose Keep editing to stay.',
          'Next, open the job in Project Report → Job actions to fill Work Details, raise the Quotation and assign work.',
        ],
      },
    ],
    related: [
      { label: 'Project Report', path: '/work/report' },
      { label: 'New Job Creation', path: '/work/projects/new' },
      { label: 'Projects', path: '/work/projects' },
    ],
  },
  {
    path: '/work/projects/new',
    title: 'New Job Creation',
    summary:
      'A step-by-step wizard to create a job with all its details: basic details, sanction, type of work, contacts, accounting and milestones.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Basic Details: fill Name of Project, Project date, Site address and Locality. Press Next.',
          'Sanction & Quotation: sanction number and date, Sanctioning Authority, and the quotation letterhead (By the name of) and Signature. Press Next.',
          'Type of Work: choose New Work or Existing Work, then Allied Work, Building Work or Allied + Building Work. Fill the building details that appear. Press Next.',
          'POC: fill Architect, Builder/Developer, Owner and Client details. Tick "Same as …" to copy a contact you already filled. Press Next.',
          'Accounting: Job Code, Document Reference Number, how the quote is calculated, site visit charges, total, and milestones.',
          'Press Finalize work, then Finalize in the box. A message says "New Project created successfully."',
        ],
      },
      {
        title: 'Main fields',
        fields: [
          { name: 'Name of Project', help: '3 to 100 characters.', required: true },
          { name: 'Project date', help: 'Today by default. Financial year fills by itself from it.', required: true },
          { name: 'Site address', help: '3 to 300 characters.', required: true },
          { name: 'Locality', help: '3 to 100 characters.', required: true },
          { name: 'By the name of', help: 'The letterhead the quotation is printed on.', required: true },
          { name: 'New Work / Existing Work', help: 'Is this a new building or work on an existing one?', required: true },
          { name: 'Work category', help: 'Allied Work, Building Work, or Allied + Building Work.', required: true },
          { name: 'Building details', help: 'Type of building, Basement and number of basements, Ground Level, Total no. of Upper Floors (0 to 100; 0 when there are none), Provision for Additional Floors, Staircap or OHWT/LMR, Total Area (0 allowed when not known yet). Fields appear as you choose.', required: true },
          { name: 'Allied work details', help: 'Shown for allied work. Describe the work.', required: true },
          { name: 'Mobile / Phone / WhatsApp number', help: 'Exactly 10 digits, no spaces or +91.' },
          { name: 'Email address', help: 'A valid email, for example name@example.com.' },
          { name: 'Job Code', help: 'The office job number.', required: true },
          { name: 'Document Reference Number', help: 'The reference number for the job papers.', required: true },
          { name: 'Quote as per', help: 'Sq. ft. (rate × area), Percentage (of construction cost) or Lump-sum.', required: true },
          { name: 'Visit fees are', help: 'Shown when site visits are in scope. Choose whether visit fees include all expenses. Then enter the charge and the Max no. of free site visits for the Site Engineer and the Director.' },
          { name: 'No. of Milestones', help: 'How many payment stages. With 4 you get the standard split 10 / 40 / 40 / 10 %. Each milestone needs a percentage and a name. The total must be 100%.', required: true },
          { name: 'Notes', help: 'Each step has an optional Notes box, up to 1000 characters.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Next checks the fields of the current step. Fix the red fields to move on.',
          'Your typing is saved as a draft in this browser. Next time you open the wizard it asks "Resume draft?". Press Resume to continue or Discard to start fresh.',
          'The draft is only in this browser on this computer. It is not shared.',
          'GST (18%) and the Gross total are worked out for you.',
          'The Quotation and File Section steps are "coming soon" and have nothing to fill yet.',
          'You can still edit the job later from Project Report.',
          'If this page opens as "unauthorized", your role cannot create jobs — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Add Project', path: '/work/projects/add' },
      { label: 'Project Report', path: '/work/report' },
    ],
  },
  {
    path: '/work/projects/:projectId',
    title: 'Project details',
    summary:
      'One project. Analysts see the job summary, milestones and work assignment. Employees see the tasks given to them on this project.',
    sections: [
      {
        title: 'Analyst view',
        bullets: [
          'The top card shows Job Code, date, name, status, Job Owner, Job value and Milestones (done / total).',
          'The steps below are Job Information, Milestones, Work Progress and Completed. Click a step to open it.',
          'Milestones: click a milestone in Milestone Details and switch on "Milestone achieved".',
          'Work Progress → Assign Work: the Work Summary lists every task of the job. Unassigned tasks have an Assign Work button.',
          'Job Information, In Progress, Work Approval and Completed show "Coming Soon" for now.',
        ],
      },
      {
        title: 'Step by step: assign a task (analyst)',
        steps: [
          'Open Work Progress, then Assign Work.',
          'Press Assign Work on the task card.',
          'Choose the Employee. Press view schedule to see their free hours and pick the week.',
          'Set the Work timeline (hours : minutes), the Priority, any Inward Documents, and write the Note/Comment.',
          'Press Assign Work. The card now shows who has the task. Use its edit or delete buttons to change it.',
        ],
      },
      {
        title: 'Fields (task assignment)',
        fields: [
          { name: 'Employee', help: 'Who will do the task, and for which week. The hours left for that week are shown.', required: true },
          { name: 'Work timeline', help: 'Time given for the task. Must be more than zero and fit in the employee\'s free hours.', required: true },
          { name: 'Priority', help: 'How urgent the task is.', required: true },
          { name: 'Inward Documents', help: 'Files from the client that the employee needs. Optional.' },
          { name: 'Note/Comment', help: 'What to do. Up to 500 characters.', required: true },
        ],
      },
      {
        title: 'Employee view',
        bullets: [
          'The top card shows the project details.',
          'Each task card shows Job code, Project name, Work timeline, Priority, Inward documents and Note / Comment.',
          'Press Play to start and Pause to stop work on a task.',
          'Add file on a task card hands your file in for that task: choose the file, add a note, and submit. It then shows on the card.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Milestones are reached in order. A later milestone stays disabled until the earlier one is achieved.',
          'Once an invoice is sent for a milestone, it is complete and cannot be switched off here.',
          'Only one task card can be edited at a time. Finish or Cancel it before opening another.',
          'Roles that may edit projects (admin, manager) see the full project with its tasks and assignments. Others see the tasks of the project assigned to them.',
          'For new work, the Tasks and Assign Work screens are the main way to plan and track hours.',
        ],
      },
    ],
    related: [
      { label: 'Projects', path: '/work/projects' },
      { label: 'Assign Work', path: '/work/assign' },
      { label: 'Tasks', path: '/tasks' },
    ],
  },
  {
    path: '/jobs/:id',
    title: 'Job view (older screen)',
    summary:
      'The older job page. It opens from "view details" in the older job lists and shows the job by your role: analyst, employee or accounts.',
    sections: [
      {
        title: 'What each role sees',
        bullets: [
          'Analyst: job code, value, milestones, Inward / Outward / Financial Documents, site visit reports and images, work assignment and approval, and revision requests.',
          'Employee: the work assigned to you on this job, its documents and notes, and a box to Add comment.',
          'Accounts: milestone details, 1. Proforma and 2. Invoice, payment requests, and Add Credit to record money received (amount, date, GST and TDS).',
        ],
      },
      {
        title: 'Step by step',
        steps: [
          'Open the job from "view details" in a job list.',
          'Read the top card for the job code, value and milestones.',
          'Scroll to the part you need and use its buttons.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'This old address now opens the project page.',
          'New work is planned on Tasks and Assign Work. Prefer those screens for new tasks.',
        ],
      },
    ],
    related: [
      { label: 'Project details', path: '/work/projects/:projectId' },
      { label: 'Project Report', path: '/work/report' },
    ],
  },
  {
    path: '/work/report',
    title: 'Project Report',
    summary:
      'Search and filter all jobs, export them to Excel, and (if allowed) edit a job or open its Work Details, Quotation and Assign Work. The screen title is WORK JOB REPORT.',
    sections: [
      {
        title: 'Step by step: find jobs',
        steps: [
          'Type in Search: job code, name, locality, address, client, architect, builder or owner. The list updates when you stop typing.',
          'Or choose one or more filters: Project Type, Construction Work Type, Building Type, Status, Financial Year, Client, Architect, Builder, Owner.',
          'The list shows only when there is a search or at least one filter.',
          'Press Export to Excel to download exactly the jobs on screen, with all columns.',
          'Press Clear Filters to start again.',
        ],
      },
      {
        title: 'Step by step: set up a new job fully',
        steps: [
          'Press the pencil (Edit job). Make sure Construction Work Type is set. Save.',
          'Press the "…" button (Job actions). Press Work Details. Fill the building details and press Submit.',
          'In Job actions press Quotation. Set the Quotation Date (today by default; an earlier date is allowed, a future one is not) and Quotation To (Client, Architect, Builder or Owner: their name and address head the PDF). Fill the quote and milestones. Press Raise Quotation.',
          'An admin or analyst presses Approve on the quotation in the list at the top. Bills can then be raised from it.',
          'In Job actions press ASSIGN WORK to create tasks for the team.',
        ],
      },
      {
        title: 'Fields (Edit job)',
        fields: [
          { name: 'Job Code / Job Name / Job Date', help: 'Same rules as on Add Project.', required: true },
          { name: 'Financial Year', help: 'Filled from the Job Date.' },
          { name: 'Locality / Site Address', help: 'Where the site is.', required: true },
          { name: 'Company, Project Type, Construction Work Type, Building Type', help: 'What the job is. Construction Work Type is needed for Work Details.' },
          { name: 'Status', help: 'Moving to Canceled asks for a Cancellation Reason.' },
          { name: 'Client / Architect / Builder / Owner', help: 'The job\'s contacts. Use + Add for a new person.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'The Actions column (pencil and "…") shows only for roles allowed to edit jobs. If you don\'t see it, your role doesn\'t have that action — ask the admin.',
          'The three Job actions go in order. Work Details needs a Construction Work Type. Quotation and ASSIGN WORK stay grey until Work Details are saved.',
          'ASSIGN WORK shows only for roles that may assign work.',
          'Each Raise Quotation adds a new version. The newest one is marked Current. Older ones stay in the list.',
          'Proceed with Minimum (₹25,000) makes ₹25,000 the fee: Gross Total and the milestones are worked out from it. Proceed with Actual uses the calculated total.',
          'Each quotation row has PDF buttons: with letterhead, and without letterhead for pre-printed paper.',
          'Only an admin or analyst can Approve a quotation or withdraw the approval.',
        ],
      },
    ],
    related: [
      { label: 'Assign Work', path: '/work/assign' },
      { label: 'Add Project', path: '/work/projects/add' },
      { label: 'Projects', path: '/work/projects' },
    ],
  },
  {
    path: '/work/assign',
    title: 'Assign Work',
    summary:
      'Give drawing and design tasks, or a site visit, to an employee for a job. Below the form you see the job\'s current assignments and a week calendar of all assigned work.',
    sections: [
      {
        title: 'Step by step: assign tasks',
        steps: [
          'Keep Tasks selected at the top.',
          'Choose the Job (type to search).',
          'Choose the Department to narrow the list, then the Employee.',
          'Leave "Employee must accept" on if the employee should accept before starting.',
          'Fill the task: Title, Priority, Work Activity, Type of Work Activity, Description, Start date, Due date, Allocated hours.',
          'Press Add task for another task for the same employee. The bin icon removes a task row.',
          'Press Assign Tasks. A message lists the task codes created, for example TASK-0042.',
        ],
      },
      {
        title: 'Step by step: assign a site visit',
        steps: [
          'Select Site Visit at the top.',
          'Choose the Job and the Site Engineer.',
          'Pick the Visit Date and Shift, and the Visit Count.',
          'Tick Free visit, Director visit or Analyst visit if they apply.',
          'Press Assign Site Visit.',
        ],
      },
      {
        title: 'Fields (task row)',
        fields: [
          { name: 'Job', help: 'The project the work is for.', required: true },
          { name: 'Employee', help: 'Who does the work.', required: true },
          { name: 'Title', help: 'A short name, for example "S1 slab drawing".', required: true },
          { name: 'Priority', help: 'Low, Medium, High or Urgent. Medium by default.' },
          { name: 'Work Activity / Type of Work Activity', help: 'What kind of work. Pick the activity first; the type list then shows its types.' },
          { name: 'Shuttering / Framing', help: 'Opens only for a type that needs it. Choose one.' },
          { name: 'Structural drawings', help: 'Opens only for a type that needs a structural drawing. Pick from the job\'s drawings (C1, C2… column footing; S1, S2… structural). They come from the job\'s Work Details.' },
          { name: 'Description', help: 'What exactly to do. Optional.' },
          { name: 'Start date / Due date', help: 'Today by default. Due date cannot be before the start date.' },
          { name: 'Allocated hours', help: '8 by default. Planned evenly over the working days between the dates. Cannot be negative.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'If a task would put the employee over their hours, a box "Over capacity" asks you. Press Plan anyway to go ahead, or Cancel.',
          'Tasks are created one by one. If one fails, the ones before it are already created; the message says which.',
          'Tasks made here appear on the employee\'s My Work and in Tasks, with full history.',
          'The Assigned Work Calendar shows site visits on their day. Tasks show in a row above the days because they belong to a week.',
          'This screen also opens from Project Report → Job actions → ASSIGN WORK, with the job already chosen.',
          'If you can\'t open this screen, your role doesn\'t have that action — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Tasks', path: '/tasks' },
      { label: 'Workload', path: '/workload' },
      { label: 'Project Report', path: '/work/report' },
    ],
  },
  {
    path: '/site-visits',
    title: 'Site Visits',
    summary:
      'Plan site visits for engineers and directors, follow them, and see the register of visits done against the free visits in each quotation.',
    sections: [
      {
        title: 'Step by step: schedule a visit',
        steps: [
          'Press Schedule visit.',
          'Choose the Project (search by code, name or location).',
          'Choose Site engineer or Director, then the Visiting engineer.',
          'Pick the Visit date and the Time (First half, Second half or Full day).',
          'Choose the Purpose. For a new purpose choose Other… and type it in Purpose of the visit.',
          'Click the Stages to inspect (floors and slabs of the project).',
          'Check the Checklist. It builds itself from the stages and the purpose. Add, remove or reorder items.',
          'Write Instructions for the engineer and Attach drawings if needed.',
          'Press Schedule visit. The new visit page opens.',
        ],
      },
      {
        title: 'Fields (Schedule site visit)',
        fields: [
          { name: 'Project', help: 'The job to visit.', required: true },
          { name: 'Site engineer / Director', help: 'Who is visiting.' },
          { name: 'Visiting engineer', help: 'The person going to site.', required: true },
          { name: 'Visit date', help: 'The day of the visit.', required: true },
          { name: 'Time', help: 'First half, Second half or Full day.' },
          { name: 'Purpose', help: 'Why you visit. It adds that purpose\'s checklist items.', required: true },
          { name: 'Stages to inspect', help: 'Click to select. Each stage adds a "work as per drawing" item.' },
          { name: 'Checklist', help: 'At least one item is needed. Type in "Add a checklist item" and press Add.', required: true },
          { name: 'Instructions for the engineer', help: 'Anything special to check or carry. Optional.' },
          { name: 'Drawings / attachments', help: 'Images, PDF, Word or Excel files for the engineer. Optional.' },
        ],
      },
      {
        title: 'Visits and Register tabs',
        bullets: [
          'Visits: filter by Status, Engineer, Project, From and To dates. Click a row to open the visit.',
          'Progress shows checks done / total, the number of issues (red), and photos.',
          'Register → By project: Scheduled, Done, Cancelled, Engineer visits / free and Director visits / free. "+N chargeable" in red means extra visits beyond the free ones in the quotation.',
          'Register → By engineer: Scheduled, Done, Pending (past date) and Issues found.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Statuses: Scheduled → In progress → Submitted → Reviewed. A visit can be Cancelled at any time before it is Reviewed.',
          'The engineer normally does the visit and the report in the mobile app.',
          'Checklists opens the list of checklist items for each purpose.',
          'A visit cannot be moved to another project. Cancel it and schedule a new one.',
        ],
      },
    ],
    related: [
      { label: 'Site visit details', path: '/work/site-visits/:id' },
      { label: 'Site Visit Checklists', path: '/work/site-visits/checklists' },
    ],
  },
  {
    path: '/work/site-visits/:id',
    title: 'Site visit details',
    summary:
      'One site visit: what was planned, what the engineer found (checklist, photos, report), and the office\'s next step — edit, start, cancel, review, send back, or the PDF report.',
    sections: [
      {
        title: 'Buttons at the top',
        bullets: [
          'Edit: change the visit while it is still Scheduled.',
          'Start: start the visit for the engineer (for a visit reported by phone).',
          'Cancel visit: give a reason. Not possible after it is Reviewed.',
          'Send back: when Submitted, return it to the engineer and say what needs to change.',
          'Mark reviewed: when Submitted, accept the report. A review note is optional.',
          'PDF report: the visit report to share with the client.',
          'If you don\'t see a button, the visit is in a status where it does not apply, or your role doesn\'t have that action — ask the admin.',
        ],
      },
      {
        title: 'Step by step: fill the report for the engineer',
        steps: [
          'Press Start if the visit is still Scheduled.',
          'For each checklist item, choose OK, Issue or N/A. For an Issue, write a Remark.',
          'Use Add photo on an item, or under Site photos for general photos.',
          'Fill the Report: Stage of work at site, Persons met, Observations, Instructions given at site, Outcome.',
          'Press Save draft to keep your work, or Submit report when it is complete.',
        ],
      },
      {
        title: 'Fields (Report)',
        fields: [
          { name: 'Stage of work at site', help: 'What stage the construction is at.' },
          { name: 'Persons met', help: 'Names of people met at site.' },
          { name: 'Observations', help: 'What was seen at site.', required: true },
          { name: 'Instructions given at site', help: 'What was told to the contractor or supervisor.' },
          { name: 'Outcome', help: 'OK to proceed, Proceed after corrections, or Not OK – revisit needed.', required: true },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Submit report is refused until every checklist item is answered, every Issue has a remark, Observations are filled and an Outcome is chosen. Your draft is kept.',
          'Items from the office checklist cannot be removed on site. Mark them N/A instead.',
          'After Send back the visit goes back to In progress, and the engineer gets a notice.',
          'The report is normally written by the engineer in the mobile app. Fill it here only on their behalf.',
        ],
      },
    ],
    related: [
      { label: 'Site Visits', path: '/site-visits' },
      { label: 'Site Visit Checklists', path: '/work/site-visits/checklists' },
    ],
  },
  {
    path: '/work/site-visits/checklists',
    title: 'Site Visit Checklists',
    summary:
      'The standard checklist items for each visit purpose. When a visit is scheduled, the purpose copies its active items onto the visit.',
    sections: [
      {
        title: 'Step by step: add a checklist item (admin)',
        steps: [
          'Press Add Checklist item.',
          'Type the Purpose. Use exactly the same words as other items of that checklist.',
          'Type the Checklist item and What to check.',
          'Set the Order (smaller numbers come first) and tick Active.',
          'Press Save.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Purpose', help: 'Items with the same purpose make one checklist, for example "Slab / beam before casting".', required: true },
          { name: 'Checklist item', help: 'The point to check, in a few words.', required: true },
          { name: 'What to check (shown to the engineer)', help: 'A short hint for the engineer. Optional.' },
          { name: 'Order', help: 'Position in the list. 10 by default.' },
          { name: 'Active', help: 'Only active items are added to new visits.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Analysts can read this list. Only an admin can add, edit or delete. If you don\'t see Add Checklist item, your role doesn\'t have that action — ask the admin.',
          'A change here affects visits scheduled after it. Visits already scheduled keep their items.',
          'To stop using an item, untick Active instead of deleting it.',
          'Press Site visits at the top to go back.',
        ],
      },
    ],
    related: [{ label: 'Site Visits', path: '/site-visits' }],
  },

  // ════════════════════════════════════════════════════════════════════
  // Tasks
  // ════════════════════════════════════════════════════════════════════
  {
    path: '/tasks',
    title: 'Tasks',
    summary:
      'All tasks you may see, with search, filters and sorting. Managers see everyone\'s tasks; employees see their own.',
    sections: [
      {
        title: 'Step by step: find a task',
        steps: [
          'Type in "Search code, title, project" and press Enter.',
          'Narrow with Status, Priority and Project. Managers also have Employee and Department.',
          'Turn on Overdue to see only late tasks. Turn on Include closed to also see completed and cancelled tasks.',
          'Click a column title (Priority, Status, Due, Allocated, Actual) to sort.',
          'Click a row to open the task.',
        ],
      },
      {
        title: 'Step by step: create a task',
        steps: [
          'Press New task.',
          'Choose the Project.',
          'Choose the Department and Employee (optional). Leave "Employee must accept" on if they should accept first.',
          'Fill Title, Priority, Work Activity, Type of Work Activity, Description, Start date, Due date and Allocated hours.',
          'Press Create. The new task opens.',
        ],
      },
      {
        title: 'Fields (NEW TASK)',
        fields: [
          { name: 'Project', help: 'The job the task belongs to.', required: true },
          { name: 'Department / Employee (optional)', help: 'Who will do it. Leave empty to keep the task unassigned and assign it later.' },
          { name: 'Employee must accept', help: 'On: the employee must press Accept before starting.' },
          { name: 'Title', help: 'Short name of the work.', required: true },
          { name: 'Priority', help: 'Low (green), Medium (yellow), High (red) or Urgent (dark red).' },
          { name: 'Work Activity / Type of Work Activity', help: 'The kind of work. Some types open Shuttering / Framing or structural drawing fields.' },
          { name: 'Start date / Due date', help: 'Due date cannot be before start date.' },
          { name: 'Allocated hours', help: 'The hours budget. Planned evenly over the working days between the dates.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'New task shows only for roles that may create tasks. If you don\'t see it, your role doesn\'t have that action — ask the admin.',
          '"Unassigned" in the Assignee column means nobody owns the task yet.',
          'The default list shows open tasks only, sorted by due date.',
          'Hours: Allocated is the budget, Actual is logged time, Remaining is what is left.',
        ],
      },
    ],
    related: [
      { label: 'Task details', path: '/tasks/:id' },
      { label: 'Assign Work', path: '/work/assign' },
      { label: 'Workload', path: '/workload' },
    ],
  },
  {
    path: '/tasks/:id',
    title: 'Task details',
    summary:
      'Everything about one task: its status, hours, people, time entries, comments and files, and every action you may take on it.',
    sections: [
      {
        title: 'The status trail',
        body: 'The bar at the top shows where the task is: Created → Accepted → Started / Stopped → In Progress → On Hold → Done → Completed. Green ticks are finished steps. The coloured step is the current one. On Hold lights up only while the task is on hold or blocked.',
      },
      {
        title: 'Step by step: work on a task (employee)',
        steps: [
          'If a blue box says the task is assigned to you, press Accept. Press Decline if you cannot take it, and say why.',
          'Press Start to start the timer. Press Pause for a break and Stop when you finish for the day.',
          'Or press Log time to add time by hand.',
          'Use Status → In progress, On hold or Blocked… to show where you are. Blocked asks what it is blocked on.',
          'Post questions in Comments. Attach your files in Attachments.',
          'When the work is ready, press Status → Submit for review, then Yes, submit.',
        ],
      },
      {
        title: 'Step by step: manage a task (manager)',
        steps: [
          'Edit: change title, priority, dates, hours, work activity.',
          'Assign: give an unowned task to someone (Employee, Allocated hours, Start, Due).',
          'Reassign: move the task to another person with a Reason. Only the hours left move; logged hours stay with the first person.',
          'Daily plan → Edit plan: set the hours for each day. The total cannot be more than the allocated hours.',
          'When the work is submitted, use Status → Complete, or Request revision to send it back.',
          'Status → Cancel task… ends the work. Give a reason. Open subtasks are cancelled too.',
        ],
      },
      {
        title: 'Tabs',
        bullets: [
          'Assignments, Reassignments, Daily plan: who held the task, hand-overs, and the day-by-day plan. Not shown to employees.',
          'Time entries: every time record on the task.',
          'Dependencies: tasks this one waits on, or blocks. Add dependency needs the other task\'s number (TASK-0042 → 42).',
          'Subtasks: smaller tasks under this one. Add subtask uses the same project.',
          'Comments, Attachments, Activity: discussion, files and the full history.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Buttons show only when you may use them. If you don\'t see Edit, Assign, Reassign or Delete, your role doesn\'t have that action — ask the admin.',
          'After Submit for review the timer cannot be restarted until a revision is requested.',
          'A task "blocked by" another task cannot start until that task is completed.',
          'A task cannot be completed while it has open subtasks.',
          'Allocated hours cannot be set below the hours already logged.',
          'Request release asks a manager to take the task off you. Give a Reason; you may suggest who could take over.',
          'Delete works only for a task with no logged time or history. Type DELETE to confirm.',
          'An employee restarts a paused task from My Work.',
        ],
      },
    ],
    related: [
      { label: 'Tasks', path: '/tasks' },
      { label: 'My Work', path: '/my-work' },
      { label: 'Reassignments', path: '/tasks/reassignments' },
    ],
  },
  {
    path: '/tasks/reassignments',
    title: 'Reassignments',
    summary:
      'Hand-overs of tasks between people. Answer tasks offered to you, follow your own release requests, and (managers) approve or reject requests.',
    sections: [
      {
        title: 'Step by step: answer an offer',
        steps: [
          'WAITING FOR YOUR ANSWER lists tasks someone is handing to you, with the hours and the answer-by date.',
          'Press Accept to take the task. It joins your My Work.',
          'Or press Decline and write why.',
        ],
      },
      {
        title: 'Step by step: review a request (manager)',
        steps: [
          'In ALL REQUESTS, keep Status on Requested.',
          'Press Review on a row. The APPROVE RELEASE & REASSIGN box opens.',
          'Choose Assign to (the new owner) and check the Transferred hours.',
          'Press Approve & reassign. Or click "approve the release without a new owner" to put it back in the pool and assign later.',
          'Press Reject to refuse the request. Add remarks.',
        ],
      },
      {
        title: 'Fields (APPROVE RELEASE & REASSIGN)',
        fields: [
          { name: 'Assign to', help: 'The new owner. Not the current one.', required: true },
          { name: 'Transferred hours', help: 'Hours that move to the new owner. At most what is left of the current assignment.' },
          { name: 'Remarks', help: 'A note for the record. Optional.' },
          { name: 'New owner must accept', help: 'Shown only to managers allowed to skip acceptance.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Statuses: Requested, Approved (waiting for the new owner), Accepted, Reassigned, Released, Rejected, Declined, Cancelled.',
          'The new owner has 3 days to accept. After that the offer shows "expired" and cannot be accepted.',
          'A manager can Withdraw an approved offer that is still waiting.',
          'You can Withdraw your own request while it is still Requested (MY RELEASE REQUESTS).',
          'Logged hours always stay with the person who worked them.',
          'ALL REQUESTS shows only for managers. If you don\'t see it, your role doesn\'t have that action.',
        ],
      },
    ],
    related: [
      { label: 'Task details', path: '/tasks/:id' },
      { label: 'Workload', path: '/workload' },
    ],
  },
  {
    path: '/workload',
    title: 'Workload',
    summary:
      'For managers: who is overloaded, who has free time, and where the hours go — by team, department and the whole organization.',
    sections: [
      {
        title: 'Tabs',
        bullets: [
          'Team: one day. Pick the Date and Department. Tiles show Team size, Active tasks, Completed today, Overdue, Blocked, Pending reassignments, Overloaded and Underutilized.',
          'Departments: one row per department for the From – To dates. Click a row to see that team.',
          'Organization: totals for the whole firm.',
          'Admin dashboard: task counts, departments, and lists of overloaded and underutilized employees.',
        ],
      },
      {
        title: 'Step by step: balance the team',
        steps: [
          'Open Team and pick the date.',
          'Look at the Status column in EMPLOYEE WORKLOAD. Find Overloaded and Underutilized people.',
          'Click a person to see their week day by day. Press Open tasks to see their tasks.',
          'Move work with Reassign on the task, or plan new work for people with free hours.',
          'Under RELEASE REQUESTS WAITING, press Review to handle hand-over requests.',
        ],
      },
      {
        title: 'Reading the numbers',
        bullets: [
          'Capacity: working hours (by default 8.5 h a day, Monday to Saturday, less leave and holidays).',
          'Allocated: hours planned. Actual: hours logged. Utilization: allocated ÷ capacity.',
          'Underutilized: 70% or less. Normal: up to 90%. High: up to 100%. Overloaded: over 100%. No capacity: a day with no working hours.',
          'Active alloc.: hours still to do on open tasks. Released hours are not counted twice.',
          'Some tiles are links: Overdue, Blocked and Pending reassignments open the matching list.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Workload views are for managers. Others see a message; your own workload is on My Work.',
          'Departments, Organization and Admin dashboard show only for roles with organization view. If you don\'t see them, your role doesn\'t have that action — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'Tasks', path: '/tasks' },
      { label: 'Reassignments', path: '/tasks/reassignments' },
      { label: 'Assign Work', path: '/work/assign' },
    ],
  },
  {
    path: '/team-timesheets',
    title: 'Team Timesheets',
    summary:
      'For managers: the weekly timesheets your team has submitted. Check each week, then approve it or return it for correction.',
    sections: [
      {
        title: 'Step by step: review a week',
        steps: [
          'Each card shows the employee, the week, and hours logged out of capacity.',
          'Press Details to see the week grid and every entry. Press Hide to close it.',
          'If it is correct, press Approve.',
          'If something is wrong, press Return. In "What needs correcting?" write the reason, then press Return.',
        ],
      },
      {
        title: 'Fields (RETURN TIMESHEET)',
        fields: [
          { name: 'What needs correcting?', help: 'Tell the employee exactly what to fix, for example "Tuesday 3 hours missing on TASK-0042".', required: true },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'The title shows how many weeks are waiting, for example TEAM TIMESHEETS (3).',
          'You cannot review your own timesheet.',
          'Only a submitted week can be approved or returned.',
          'A returned week opens again for the employee. They fix it on My Timesheet and submit again.',
          'An approved week stays locked. Its time cannot be changed.',
          'If you see "Reviewing timesheets is for managers", your role cannot review — ask the admin.',
        ],
      },
    ],
    related: [
      { label: 'My Timesheet', path: '/timesheet' },
      { label: 'Workload', path: '/workload' },
    ],
  },
];
